import { Fragment, type ReactElement } from "react";
import { type Atom, useSelector } from "@tanstack/react-store";
import type { RowData, SortDirection } from "@tanstack/react-table";
import { ArrowDownIcon, ArrowUpIcon, DotsHorizontalIcon } from "@icons";
import { Menu } from "@design-system/Menu/Menu.lib";
import { getVariantValue } from "../cells/cellVariant";
import type {
    DataGridColumnInstance,
    DataGridColumnVariants,
    DataGridResolvedColumnMeta,
} from "../columns/types";
import styles from "./Header.module.scss";

export interface HeaderMenuEntry {
    id: string;
    group: "sort" | "column";
    label: string;
    leading?: ReactElement;
    isDisabled?: boolean;
    onSelect: () => void;
}

/** Sort action labels, shared by the header menu and the header's sort tooltip. */
export const SORT_ACTION_LABELS = {
    asc: "Sort ascending",
    desc: "Sort descending",
    clear: "Clear sort",
} satisfies Record<SortDirection | "clear", string>;

const getSortEntries = <TData extends RowData>(
    column: DataGridColumnInstance<TData>,
    sortDirection: SortDirection | false,
): HeaderMenuEntry[] =>
    column.getCanSort()
        ? [
              {
                  id: "sort-asc",
                  group: "sort",
                  label: SORT_ACTION_LABELS.asc,
                  leading: <ArrowUpIcon />,
                  onSelect: () => column.toggleSorting(false, false),
              },
              {
                  id: "sort-desc",
                  group: "sort",
                  label: SORT_ACTION_LABELS.desc,
                  leading: <ArrowDownIcon />,
                  onSelect: () => column.toggleSorting(true, false),
              },
              {
                  id: "sort-clear",
                  group: "sort",
                  label: SORT_ACTION_LABELS.clear,
                  isDisabled: !sortDirection,
                  onSelect: () => column.clearSorting(),
              },
          ]
        : [];

/**
 * A sortable header's sort entries (each replaces the whole sort, unlike
 * Shift+click), then the column's own items.
 */
export const getHeaderMenuEntries = <TData extends RowData>({
    column,
    meta,
    sortDirection,
}: {
    column: DataGridColumnInstance<TData>;
    meta: DataGridResolvedColumnMeta;
    /** The column's current sort, from the header (`column` itself is stable). */
    sortDirection: SortDirection | false;
}): HeaderMenuEntry[] => [
    ...getSortEntries(column, sortDirection),
    ...meta.menuItems.map((item): HeaderMenuEntry => ({
        ...item,
        group: "column",
    })),
];

/** A column's `variants` as its header menu's radio group. */
export interface HeaderMenuVariant {
    value: string;
    options: DataGridColumnVariants["options"];
    onValueChange: (value: string) => void;
}

/** The radio group for a column with `variants`; picking one updates `columnVariants`. */
export const useHeaderMenuVariant = ({
    columnId,
    variants,
    columnVariants,
}: {
    columnId: string;
    variants: DataGridColumnVariants | undefined;
    columnVariants: Atom<Record<string, string>>;
}): HeaderMenuVariant | undefined => {
    const chosen = useSelector(columnVariants, (all) => all[columnId]);
    return (
        variants && {
            value: getVariantValue(variants, chosen),
            options: variants.options,
            onValueChange: (value) =>
                columnVariants.set((all) => ({ ...all, [columnId]: value })),
        }
    );
};

/** Sorting alone is the header click, so a sort-only menu isn't shown. */
export const hasHeaderMenu = (
    entries: readonly HeaderMenuEntry[],
    variant: HeaderMenuVariant | undefined,
) => variant !== undefined || entries.some((entry) => entry.group !== "sort");

const renderEntries = (entries: readonly HeaderMenuEntry[]) =>
    entries.map((entry) => (
        <Menu.Item
            key={entry.id}
            leading={entry.leading}
            disabled={entry.isDisabled}
            onClick={entry.onSelect}
        >
            {entry.label}
        </Menu.Item>
    ));

interface HeaderMenuProps {
    entries: readonly HeaderMenuEntry[];
    variant: HeaderMenuVariant | undefined;
    label: string;
    /** In the Tab order only while focus is inside the header cell. */
    isTabbable: boolean;
}

/**
 * Column menu on a header whose menu offers more than sorting: the sort
 * entries, the variant radio group, then the column's own items, with a
 * separator between each part.
 */
export const HeaderMenu = ({
    entries,
    variant,
    label,
    isTabbable,
}: Readonly<HeaderMenuProps>) => {
    const sortEntries = entries.filter((entry) => entry.group === "sort");
    const columnEntries = entries.filter((entry) => entry.group !== "sort");
    const parts = [
        {
            id: "sort",
            node: sortEntries.length > 0 && renderEntries(sortEntries),
        },
        {
            id: "variant",
            node: variant && (
                <Menu.RadioGroup
                    value={variant.value}
                    onValueChange={variant.onValueChange}
                >
                    {variant.options.map((option) => (
                        <Menu.RadioItem key={option.value} value={option.value}>
                            {option.label}
                        </Menu.RadioItem>
                    ))}
                </Menu.RadioGroup>
            ),
        },
        {
            id: "column",
            node: columnEntries.length > 0 && renderEntries(columnEntries),
        },
    ].filter((part) => part.node);
    return (
        <Menu.Root
            align="end"
            trigger={
                // Raw button: Button has no 20px chromeless size, and the
                // header reveals this trigger on hover.
                <button
                    type="button"
                    className={styles.menuTrigger}
                    aria-label={`${label} options`}
                    data-dt-no-drag=""
                    tabIndex={isTabbable ? 0 : -1}
                >
                    <DotsHorizontalIcon aria-hidden />
                </button>
            }
        >
            {parts.map((part, index) => (
                <Fragment key={part.id}>
                    {index > 0 ? <Menu.Separator /> : null}
                    {part.node}
                </Fragment>
            ))}
        </Menu.Root>
    );
};
